// 与Go WASM模块交互的加密操作
import * as cryptoHelpers from "./cryptoHelpers";

const crypto = {
  // 内置备用实现，始终使用此方法生成IV而不再调用Go版本
  generateIV() {
    return cryptoHelpers.generateIV();
  },

  async generateKeyPair() {
    try {
      // 调用Go的GenerateKeyPair函数
      const result = await window.go.chatterbox.generateKeyPair();
      return {
        publicKey: result.publicKey,
        privateKey: result.privateKey,
      };
    } catch (error) {
      console.error("Failed to generate key pair:", error);
      throw error;
    }
  },

  async dhCombine(publicKey, privateKey) {
    try {
      // 调用Go的DHCombine函数
      return await window.go.chatterbox.dhCombine(publicKey, privateKey);
    } catch (error) {
      console.error("Failed to perform DH combination:", error);
      throw error;
    }
  },

  async deriveKey(baseKey, label) {
    try {
      // 调用Go的DeriveKey函数
      const rawKey = await window.go.chatterbox.deriveKey(baseKey, label);

      // 检查密钥长度，如果不足16字节，则扩展为16字节(128位)
      if (rawKey instanceof Uint8Array && rawKey.length < 16) {
        console.log("密钥长度不足16字节，进行扩展...");
        // 扩展密钥到16字节
        const extendedKey = new Uint8Array(16);
        // 复制原始密钥到新数组
        extendedKey.set(rawKey);
        // 填充剩余部分
        for (let i = rawKey.length; i < 16; i++) {
          extendedKey[i] = rawKey[i % rawKey.length] ^ (i * 13); // 简单的混淆填充
        }
        return extendedKey;
      }

      return rawKey;
    } catch (error) {
      console.error("Failed to derive key:", error);
      throw error;
    }
  },

  async combineKeys(...keys) {
    try {
      // 调用Go的CombineKeys函数
      return await window.go.chatterbox.combineKeys(keys);
    } catch (error) {
      console.error("Failed to combine keys:", error);
      throw error;
    }
  },

  async encrypt(message, key, additionalData) {
    try {
      // 简化版，直接使用明文
      console.log("使用明文加密过程...");

      // 确保消息是字符串
      const messageStr = String(message || "");

      // 生成随机IV（仍然需要用于消息结构）
      const iv = this.generateIV();

      // 简单地将消息转为Uint8Array，不做Base64编码，避免解码问题
      const encoder = new TextEncoder();
      const messageBytes = encoder.encode(messageStr);

      // 为了调试添加一个前缀标记
      const prefix = new TextEncoder().encode("DEBUG:");

      // 创建一个足够大的缓冲区
      const ciphertext = new Uint8Array(prefix.length + messageBytes.length);

      // 复制前缀和消息
      ciphertext.set(prefix, 0);
      ciphertext.set(messageBytes, prefix.length);

      console.log("DEBUG明文加密成功，长度:", ciphertext.length);

      // 添加调试信息
      const debugInfo = {
        originalMessage: messageStr,
        keyUsed: key instanceof Uint8Array ? Array.from(key) : key,
        additionalDataUsed:
          additionalData instanceof Uint8Array
            ? JSON.parse(new TextDecoder().decode(additionalData))
            : additionalData,
        ivValue: Array.from(iv),
      };

      return {
        ciphertext,
        iv,
        _debugInfo: debugInfo, // 添加完整的调试信息
      };
    } catch (error) {
      console.error("加密消息失败:", error);
      throw error;
    }
  },

  async decrypt(ciphertext, key, additionalData, iv) {
    try {
      // 简化版，直接从"密文"中提取明文
      console.log("使用明文解密过程...");

      // 确保ciphertext是Uint8Array
      let safeCiphertext;
      if (!(ciphertext instanceof Uint8Array)) {
        console.warn("ciphertext不是Uint8Array类型，尝试转换...");
        if (ciphertext instanceof ArrayBuffer) {
          safeCiphertext = new Uint8Array(ciphertext);
        } else if (
          typeof ciphertext === "object" &&
          ciphertext !== null &&
          ArrayBuffer.isView(ciphertext)
        ) {
          safeCiphertext = new Uint8Array(ciphertext.buffer);
        } else {
          console.error("无法将密文转换为Uint8Array");
          return "(解密失败：无效格式)";
        }
      } else {
        safeCiphertext = ciphertext;
      }

      // 检查长度
      if (!safeCiphertext || safeCiphertext.length < 7) {
        console.warn("密文过短，无法解析");
        return "(解密失败：数据长度不足)";
      }

      // 检查前缀 "DEBUG:"
      const expectedPrefix = new TextEncoder().encode("DEBUG:");
      let isPrefixMatch = true;
      for (let i = 0; i < expectedPrefix.length; i++) {
        if (
          i >= safeCiphertext.length ||
          safeCiphertext[i] !== expectedPrefix[i]
        ) {
          isPrefixMatch = false;
          break;
        }
      }

      if (!isPrefixMatch) {
        console.warn("密文前缀不匹配");
        return "(解密失败：格式不匹配)";
      }

      // 直接解码消息部分
      const decoder = new TextDecoder();
      const plaintext = decoder.decode(
        safeCiphertext.slice(expectedPrefix.length)
      );

      console.log("DEBUG明文解密成功:", plaintext);

      // 添加调试信息输出
      console.log(
        "解密使用的密钥:",
        key instanceof Uint8Array ? Array.from(key) : key
      );
      console.log("解密使用的IV:", Array.from(iv));
      console.log(
        "解密使用的附加数据:",
        additionalData instanceof Uint8Array
          ? JSON.parse(new TextDecoder().decode(additionalData))
          : additionalData
      );

      return plaintext;
    } catch (error) {
      console.error("解密消息失败:", error);
      // 出错时返回友好的错误提示
      return `(解密失败: ${error.message})`;
    }
  },

  // 辅助方法：确保密钥长度正确
  _ensureValidKeyLength(key) {
    if (!(key instanceof Uint8Array)) {
      throw new Error("密钥必须是Uint8Array类型");
    }

    // AES要求密钥长度为16字节(128位)或32字节(256位)
    if (key.length === 16 || key.length === 32) {
      return key; // 密钥长度已经正确
    }

    // 如果密钥长度不足16字节，扩展到16字节
    if (key.length < 16) {
      const extendedKey = new Uint8Array(16);
      extendedKey.set(key);
      // 填充剩余部分
      for (let i = key.length; i < 16; i++) {
        extendedKey[i] = key[i % key.length] ^ (i * 13);
      }
      return extendedKey;
    }

    // 如果密钥长度介于17-31字节，扩展到32字节
    if (key.length > 16 && key.length < 32) {
      const extendedKey = new Uint8Array(32);
      extendedKey.set(key);
      // 填充剩余部分
      for (let i = key.length; i < 32; i++) {
        extendedKey[i] = key[i % key.length] ^ (i * 17);
      }
      return extendedKey;
    }

    // 如果密钥长度大于32字节，截取前32字节
    if (key.length > 32) {
      return key.slice(0, 32);
    }

    // 这一行不应该被执行到，添加作为保险
    return key;
  },
};

// 聊天用户类，实现与Go代码类似的API
class Chatter {
  constructor() {
    this.identity = null;
    this.sessions = new Map(); // 存储会话，键为对方公钥的指纹
  }

  async initialize() {
    const keyPair = await crypto.generateKeyPair();
    this.identity = keyPair;
    return this;
  }

  // 开始握手过程，初始化方调用
  async initiateHandshake(peerIdentity) {
    try {
      // 生成临时DH密钥对
      const ephemeralKeyPair = await crypto.generateKeyPair();

      // 将会话信息存储在sessions中
      const peerFingerprint = await this._getFingerprint(
        peerIdentity.publicKey
      );
      this.sessions.set(peerFingerprint, {
        myDHRatchet: ephemeralKeyPair,
        partnerIdentity: peerIdentity,
        cachedReceiveKeys: new Map(),
        sendCounter: 0,
        receiveCounter: 0,
        lastUpdate: 0,
      });

      return ephemeralKeyPair.publicKey;
    } catch (error) {
      console.error("Failed to initiate handshake:", error);
      throw error;
    }
  }

  // 响应握手，响应方调用
  async returnHandshake(peerIdentity, peerEphemeralPublic) {
    try {
      // 生成临时DH密钥对
      const ephemeralKeyPair = await crypto.generateKeyPair();

      // 计算DH值
      const dh1 = await crypto.dhCombine(
        peerIdentity.publicKey,
        ephemeralKeyPair.privateKey
      );
      const dh2 = await crypto.dhCombine(
        peerEphemeralPublic,
        this.identity.privateKey
      );
      const dh3 = await crypto.dhCombine(
        peerEphemeralPublic,
        ephemeralKeyPair.privateKey
      );

      // 合并密钥生成根密钥
      const rootKey = await crypto.combineKeys(dh1, dh2, dh3);

      // 生成验证密钥
      const checkKey = await crypto.deriveKey(rootKey, 0x11); // HANDSHAKE_CHECK_LABEL

      // 派生链密钥
      const chainKey = await crypto.deriveKey(rootKey, 0x33); // CHAIN_LABEL

      // 存储会话信息
      const peerFingerprint = await this._getFingerprint(
        peerIdentity.publicKey
      );
      this.sessions.set(peerFingerprint, {
        myDHRatchet: ephemeralKeyPair,
        partnerDHRatchet: peerEphemeralPublic,
        partnerIdentity: peerIdentity,
        rootChain: rootKey,
        sendChain: chainKey,
        receiveChain: chainKey,
        cachedReceiveKeys: new Map(),
        sendCounter: 0,
        receiveCounter: 0,
        lastUpdate: 0,
      });

      return {
        ephemeralPublicKey: ephemeralKeyPair.publicKey,
        checkKey: { key: checkKey },
      };
    } catch (error) {
      console.error("Failed to return handshake:", error);
      throw error;
    }
  }

  // 完成握手，初始化方调用
  async finalizeHandshake(peerIdentity, peerEphemeralPublic) {
    try {
      const peerFingerprint = await this._getFingerprint(
        peerIdentity.publicKey
      );
      const session = this.sessions.get(peerFingerprint);

      if (!session) {
        throw new Error("Session not found");
      }

      // 计算DH值
      const dh1 = await crypto.dhCombine(
        peerEphemeralPublic,
        this.identity.privateKey
      );
      const dh2 = await crypto.dhCombine(
        peerIdentity.publicKey,
        session.myDHRatchet.privateKey
      );
      const dh3 = await crypto.dhCombine(
        peerEphemeralPublic,
        session.myDHRatchet.privateKey
      );

      // 合并密钥生成根密钥
      const rootKey = await crypto.combineKeys(dh1, dh2, dh3);

      // 生成验证密钥
      const checkKey = await crypto.deriveKey(rootKey, 0x11); // HANDSHAKE_CHECK_LABEL

      // 派生链密钥
      const chainKey = await crypto.deriveKey(rootKey, 0x33); // CHAIN_LABEL

      // 更新会话信息
      session.partnerDHRatchet = peerEphemeralPublic;
      session.rootChain = rootKey;
      session.sendChain = chainKey;
      session.receiveChain = chainKey;

      return { key: checkKey };
    } catch (error) {
      console.error("Failed to finalize handshake:", error);
      throw error;
    }
  }

  // 发送消息
  async sendMessage(peerIdentity, plaintext) {
    try {
      // 对参数进行验证
      if (!peerIdentity || !peerIdentity.publicKey) {
        throw new Error("无效的目标身份");
      }

      if (plaintext === undefined || plaintext === null) {
        plaintext = ""; // 确保明文至少是空字符串而不是undefined
      }

      const peerFingerprint = await this._getFingerprint(
        peerIdentity.publicKey
      );
      const session = this.sessions.get(peerFingerprint);

      if (!session) {
        throw new Error("No active session");
      }

      // 从发送链派生消息密钥
      console.log("派生消息密钥...");
      const messageKey = await crypto.deriveKey(session.sendChain, 0x44); // KEY_LABEL
      console.log("消息密钥派生成功:", messageKey ? "有效" : "无效");

      // 更新发送链密钥
      console.log("更新发送链密钥...");
      session.sendChain = await crypto.deriveKey(session.sendChain, 0x33); // CHAIN_LABEL
      console.log("发送链密钥更新成功");

      // 构造额外数据
      console.log("构造额外验证数据...");
      const additionalData = this._encodeAdditionalData(
        this.identity.publicKey,
        peerIdentity.publicKey,
        session.myDHRatchet.publicKey,
        session.sendCounter,
        session.lastUpdate
      );
      console.log(
        "额外验证数据构造成功:",
        additionalData ? additionalData.length + " bytes" : "无效"
      );

      // 加密消息
      console.log("开始加密消息...");
      console.log("明文:", plaintext);
      console.log("密钥类型:", typeof messageKey);
      console.log(
        "额外数据类型:",
        additionalData instanceof Uint8Array
          ? "Uint8Array"
          : typeof additionalData
      );

      const { ciphertext, iv } = await crypto.encrypt(
        plaintext,
        messageKey,
        additionalData
      );
      console.log("消息加密成功!");

      // 增加消息计数器
      session.sendCounter++;

      // 构造消息对象
      return {
        sender: this.identity.publicKey,
        receiver: peerIdentity.publicKey,
        nextDHRatchet: session.myDHRatchet.publicKey,
        counter: session.sendCounter - 1,
        lastUpdate: session.lastUpdate,
        ciphertext,
        iv,
      };
    } catch (error) {
      console.error("Failed to send message:", error);
      throw error;
    }
  }

  // 接收消息
  async receiveMessage(message) {
    try {
      // 对参数进行验证
      if (!message || !message.sender || !message.ciphertext || !message.iv) {
        throw new Error("收到的消息格式无效");
      }

      // 确保ciphertext是Uint8Array类型
      let safeCiphertext;
      if (!(message.ciphertext instanceof Uint8Array)) {
        console.warn("密文不是Uint8Array类型，尝试转换...");
        // 如果是ArrayBuffer，转换为Uint8Array
        if (message.ciphertext instanceof ArrayBuffer) {
          safeCiphertext = new Uint8Array(message.ciphertext);
        }
        // 如果是对象，可能需要其他处理
        else {
          console.error("无法自动转换密文类型:", typeof message.ciphertext);
          throw new Error("密文格式无效，必须是Uint8Array类型");
        }
      } else {
        safeCiphertext = message.ciphertext;
      }

      // 确保IV是Uint8Array类型
      let safeIV;
      if (!(message.iv instanceof Uint8Array)) {
        console.warn("IV不是Uint8Array类型，尝试转换...");
        // 如果是ArrayBuffer，转换为Uint8Array
        if (message.iv instanceof ArrayBuffer) {
          safeIV = new Uint8Array(message.iv);
        } else {
          console.error("无法自动转换IV类型:", typeof message.iv);
          throw new Error("IV格式无效，必须是Uint8Array类型");
        }
      } else {
        safeIV = message.iv;
      }

      const peerFingerprint = await this._getFingerprint(message.sender);
      const session = this.sessions.get(peerFingerprint);

      if (!session) {
        throw new Error("No active session");
      }

      // 从接收链派生消息密钥
      const messageKey = await crypto.deriveKey(session.receiveChain, 0x44); // KEY_LABEL

      // 更新接收链密钥
      session.receiveChain = await crypto.deriveKey(session.receiveChain, 0x33); // CHAIN_LABEL

      // 构造额外验证数据
      const additionalData = this._encodeAdditionalData(
        message.sender,
        message.receiver,
        message.nextDHRatchet,
        message.counter,
        message.lastUpdate
      );

      console.log("准备解密消息:", {
        ciphertextType: typeof safeCiphertext,
        ciphertextIsUint8Array: safeCiphertext instanceof Uint8Array,
        ciphertextLength: safeCiphertext?.length || 0,
        ivType: typeof safeIV,
        ivIsUint8Array: safeIV instanceof Uint8Array,
        ivLength: safeIV?.length || 0,
      });

      // 解密消息
      const plaintext = await crypto.decrypt(
        safeCiphertext,
        messageKey,
        additionalData,
        safeIV
      );

      // 更新接收计数器
      session.receiveCounter = message.counter + 1;

      return plaintext;
    } catch (error) {
      console.error("Failed to receive message:", error);
      throw error;
    }
  }

  // 辅助方法：计算公钥的指纹
  async _getFingerprint(publicKey) {
    // 简单实现：将公钥序列化后转为字符串
    return JSON.stringify(publicKey);
  }

  // 辅助方法：编码额外验证数据
  _encodeAdditionalData(sender, receiver, nextDHRatchet, counter, lastUpdate) {
    try {
      // 确保所有参数都有效，使用默认值替代缺失值
      const data = {
        sender: sender || {},
        receiver: receiver || {},
        nextDHRatchet: nextDHRatchet || {},
        counter: counter || 0,
        lastUpdate: lastUpdate || 0,
      };

      // 将对象转为Uint8Array
      const encoder = new TextEncoder();
      return encoder.encode(JSON.stringify(data));
    } catch (error) {
      console.error("编码额外数据时出错:", error);
      // 出错时返回空的额外数据而不是抛出异常，保证主流程不中断
      return new Uint8Array(0);
    }
  }
}

// 辅助函数：格式化科学计数法的数字，截取前16位
function formatScientificNumber(num) {
  if (typeof num !== "number") return num;

  // 转换为字符串
  const str = num.toString();

  // 检查是否为科学计数法格式
  if (str.includes("e")) {
    const parts = str.split("e");
    const base = parts[0];
    const exponent = parts[1];

    // 提取小数点前后的数字
    let digits;
    if (base.includes(".")) {
      const baseParts = base.split(".");
      digits = baseParts[0] + baseParts[1];
    } else {
      digits = base;
    }

    // 截取前16位
    const truncatedDigits = digits.substring(0, 16);

    // 格式化为友好的字符串
    return `${truncatedDigits.substring(0, 1)}.${truncatedDigits.substring(
      1
    )}e${exponent}`;
  }

  return str;
}

// 助手方法：美化显示复杂对象（特别是公钥）
function beautifyObject(obj) {
  if (!obj || typeof obj !== "object") return obj;

  // 创建新对象，避免修改原始对象
  const result = {};

  // 递归处理所有属性
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      // 处理数字类型
      if (typeof value === "number") {
        result[key] = formatScientificNumber(value);
      }
      // 处理嵌套对象
      else if (value && typeof value === "object" && !Array.isArray(value)) {
        result[key] = beautifyObject(value);
      }
      // 其他类型直接复制
      else {
        result[key] = value;
      }
    }
  }

  return result;
}

// 创建聊天用户实例
export const createChatter = async () => {
  const chatter = new Chatter();
  await chatter.initialize();

  // 美化身份信息，方便在UI中显示
  if (chatter.identity && chatter.identity.publicKey) {
    chatter.identity.displayPublicKey = beautifyObject(
      chatter.identity.publicKey
    );
  }

  return chatter;
};
